import { useEffect, useState } from 'react';
import { useTaskStore } from '@/lib/task-store';
import { useUserStore } from '@/lib/user-store';
import { useOwnerStore } from '@/lib/owner-store';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { exportTasksToExcel } from '@/utils/export-to-excel';
import { cn } from '@/lib/utils';
import { StatusBadge } from './status-badge';
import { PriorityBadge } from './priority-badge';
import { CategoryBadge } from './category-badge';
import type { Task } from '@/lib/types';

type SortField = 'title' | 'category' | 'status' | 'priority' | 'owner' | 'user' | 'due_date';
type SortDirection = 'asc' | 'desc';

interface TaskListProps {
  tasks: Task[];
  filters: {
    search: string;
    person: string;
    status: string;
    priority: string;
  };
}

export function TaskList({ tasks: propTasks, filters }: TaskListProps) {
  const { users, fetchUsers } = useUserStore();
  const { owners, fetchOwners } = useOwnerStore();
  const [sortField, setSortField] = useState<SortField>('due_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showClosedTasks, setShowClosedTasks] = useState(false);

  const resetFilters = () => {
    setShowClosedTasks(false);
  };

  useEffect(() => {
    Promise.all([fetchUsers(), fetchOwners()]);
  }, [fetchUsers, fetchOwners]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortableHeader = ({ children, field }: { children: React.ReactNode; field: SortField }) => (
    <th
      className="px-4 py-2 text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          <span className="text-xs">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  const sortedTasks = [...propTasks].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    if (sortField === 'user') {
      const userA = users.find(u => u.id === a.created_by)?.full_name || '';
      const userB = users.find(u => u.id === b.created_by)?.full_name || '';
      return userA.localeCompare(userB) * direction;
    }
    
    if (sortField === 'owner') {
      const ownerA = owners.find(o => o.id === a.owner_id)?.name || '';
      const ownerB = owners.find(o => o.id === b.owner_id)?.name || '';
      return ownerA.localeCompare(ownerB) * direction;
    }

    if (sortField === 'due_date') {
      const dateA = a.due_date ? new Date(a.due_date).getTime() : 0;
      const dateB = b.due_date ? new Date(b.due_date).getTime() : 0;
      return (dateA - dateB) * direction;
    }

    return String(a[sortField]).localeCompare(String(b[sortField])) * direction;
  }).filter(task => {
    const matchesSearch = !filters.search || 
      task.title.toLowerCase().includes(filters.search.toLowerCase()) ||
      task.description?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesPerson = !filters.person || task.created_by === filters.person;
    const matchesStatus = !filters.status || task.status === filters.status;
    const matchesPriority = !filters.priority || task.priority === filters.priority;
    const matchesClosedStatus = showClosedTasks || task.status !== 'Closed';
    
    return matchesSearch && matchesPerson && matchesStatus && matchesPriority && matchesClosedStatus;
  });

  return (
    <div className="overflow-x-auto">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowClosedTasks(!showClosedTasks)}
          >
            {showClosedTasks ? 'Hide' : 'Show'} Closed Tasks
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={resetFilters}
          >
            Reset Filters
          </Button>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => exportTasksToExcel(sortedTasks)}
        >
          Export to Excel
        </Button>
      </div>

      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <SortableHeader field="title">Title</SortableHeader>
            <SortableHeader field="category">Category</SortableHeader>
            <SortableHeader field="status">Status</SortableHeader>
            <SortableHeader field="priority">Priority</SortableHeader>
            <SortableHeader field="owner">Owner</SortableHeader>
            <SortableHeader field="user">Created By</SortableHeader>
            <SortableHeader field="due_date">Due Date</SortableHeader>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {sortedTasks.map(task => (
            <tr key={task.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
              <td className="px-4 py-2">{task.title}</td>
              <td className="px-4 py-2">
                <CategoryBadge category={task.category} />
              </td>
              <td className="px-4 py-2">
                <StatusBadge status={task.status} />
              </td>
              <td className="px-4 py-2">
                <PriorityBadge priority={task.priority} />
              </td>
              <td className="px-4 py-2">
                {task.owner?.name || 'Unassigned'}
              </td>
              <td className="px-4 py-2">
                {users.find(u => u.id === task.created_by)?.full_name || 'Unknown'}
              </td>
              <td className="px-4 py-2">
                {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No date'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}